//#endregion
import * as angular from 'angular';
import { fn } from '@common/utils';
import * as rwf3 from '@common/rwf';
import { CompatibilityService } from '@common/services/compatibilityservice';
import { GridColumn, IObservableArray, Observable } from '@common/nav';
import { IFDRW_WFInstanceCompleted, Pages, OpenInNav } from '@common/rwf';
import { GetRowItem, AsObservableArray, ReplaceObservableData, constants } from './navkendo';
import { DialogService } from './services/dialogservice';
import { IPageQueryParameters, INavReadResult } from './navdata';
import { SPAProxyController, ISPAOpenExtInfo } from './rwf.spa.proxy';
import { InfUserSettingsAccess } from '@common/services/settingsservice';
import { ExternalView } from '@common/preview/externalpreview';
import { IWindowPosition, NormalizePosInfo } from '@common/infoma.environment';

//#endregion

type CUSTOM_KEY =
    "FDRW_AWF_ORDER_DOCID" |    // Sprung aud Aufragsworkflow -> 'TextValue' = WFId
    "FDRW_BWF_ORDER_DOCID" |    // Sprung auf Bestellworkflow -> 'TextValue' = WFId
    "FDRW_RWF_DOCID" |          // Sprung auf RWF Belegauskunft
    "IUI_EINVOICE_RECID" |      // Sprung auf E-Rechnung --> Window Client, 'TextValue' = recid
    "KB_ILV_RECID" |            // Sprung auf 'Interne Verrechnung'
    "FAKTURA_APP_RECID";        // Sprung auf 'Faktura App'

const CUSTOM_TITLE: Record<CUSTOM_KEY, string> = {
    "FDRW_AWF_ORDER_DOCID": "Auftragsworkflow",
    "FDRW_BWF_ORDER_DOCID": "Bestellworkflow",
    "FDRW_RWF_DOCID": "Rechnungsworkflow",
    "IUI_EINVOICE_RECID": "e-Rechnung",
    "KB_ILV_RECID": "Interne Verrechnung",
    "FAKTURA_APP_RECID": "Faktura App"
};

type IAddonData = rwf3.IFDRW_Workflow_Additional_Info; // | rwf3.IIAX_WF_Add_Information_Sub;

/** Displaying Custom  Workflow_Add_Information (IIAX_WF_Add_Information_Sub) for IFDRW_WFInstanceCompleted/ IFDRW_WFInstance in Table Form */
export class CtrWFInstanceAddOnData extends Observable {

    constructor(readonly $c: CompatibilityService, readonly Url: string, rawAddonData?: string  | IAddonData[], userSettings?: InfUserSettingsAccess) {
        super();
        this.RefreshData(rawAddonData);

        let Ctrl: SPAProxyController;
        if (userSettings && (Ctrl = SPAProxyController.TryInstantiate($c, 'CtrlAddon', () => NormalizePosInfo(state)))) {
            var state: { OpenExt: boolean } & IWindowPosition = <any>{};
            let extDestroyed = false;
            userSettings.Add("WFInstanceAddOnData", () => state, s => state = s);
            var extInfo = this.ExtInfo = {
                get OpenExt() { return !!state.OpenExt },
                set OpenExt(value) {
                    if (value !== state.OpenExt) {
                        if (!extDestroyed) {
                            state.OpenExt = value
                        }
                    }
                },
                Ctrl: Ctrl
            }

            this.bind(constants.NavDestroy, () => { extDestroyed = true; Ctrl.IsActive = false; });
            Ctrl.bind(ExternalView.EVENT_CLOSE, () => { extInfo.OpenExt = false });
            Ctrl.bind(ExternalView.EVENT_BOUNDSCHANGED, (e: IWindowPosition) => {
                state.Offset = e.Offset;
                state.Size = e.Size;
            });
        }
    }
    private readonly ExtInfo: ISPAOpenExtInfo;
    public destroy() {
        this.trigger(constants.NavDestroy);

    }
    public GridOptions: kendo.ui.GridOptions;

    public get Show() {
        return (this.GridOptions && this.data.length) || this.Error;
    }

    private currRefresh: any;

    public Error: string;

    /** Refresh Data From AddonData of WFinstanceID **/
    public RefreshData(data: IAddonData[] | string, forceAttach?: boolean) {
        if (typeof data === "string") {
            delete this.Error;

            var currRefresh = this.currRefresh = this.$c.resolve("NavClient").ReadMultiple<"FDRW_Workflow_Additional_Info">({
                PageName: "FDRW_Workflow_Additional_Info",
                Constraints: [{ FieldName: "WFInstanceNo", Value: data }],
                PageCount: 0,
                InstanceID: data,

            }).then(data => {
                if (currRefresh = this.currRefresh) {
                    this.RefreshData(data.Data);
                }
            }).then(fn.noop, err => {
                if (currRefresh = this.currRefresh) {
                    // TODO Show Error
                    console.error(err);
                    this.RefreshData([]);
                    this.Error = fn.resolveToError(err);
                }
            })
            return;
        }
        delete this.currRefresh;
        ReplaceObservableData(this.data, data || [], item => item.Key || JSON.stringify(item));

        if (!this.GridOptions && (forceAttach || this.data.length)) {
            const
                Url = this.Url,
                $c = this.$c,
                data = this.data,
                // Addondata Display if available
                cols: GridColumn[] = [
                    { field: "Description", title: "Beschreibung" },
                    {
                        field: "TextValue", title: "Wert", encoded: false,
                        template: (e: rwf3.IIAX_WF_Add_Information_Sub & { DocInfoTitle?: string }) => {
                            let result = '{{::dataItem.TextValue}}',
                                docInfoTitle = e.DocInfoTitle = CUSTOM_TITLE[e.Key1];

                            if (docInfoTitle) {
                                let customOp: () => PromiseLike<any>;
                                switch (<CUSTOM_KEY>e.Key1) {
                                    case "IUI_EINVOICE_RECID":
                                    case "KB_ILV_RECID":
                                    case "FAKTURA_APP_RECID":
                                    var p = fn.EncodeQueryData({ navUrl: Url, recId: e.TextValue });
                                        customOp = () => OpenInNav($c, e.RecordIDValue || e.TextValue, Url);
                                        break;
                                    default:
                                        customOp = () => $c.when(OpenDocInfo(e.TextValue, this.ExtInfo));
                                        break;
                                }
                                var showInfo = e["ShowInfo"] = () => {
                                    showInfo["Disabled"] = true;

                                    $c.finally(fn.UISafe(customOp()), () => {
                                        showInfo["Disabled"] = false;
                                    });
                                };
                                result = `<button type="button" inf-progress="dataItem.Custom.Title.Disabled" ng-disabled="dataItem.ShowInfo.Disabled" ng-attr-title="{{ dataItem.DocInfoTitle + ' anzeigen ...'}}" style='width: auto' kendo-button ng-click="dataItem.ShowInfo()" class="idms_Button">
                            <span class="idms_Icon idms_Icon_Triangle_Right" ></span>
                            Anzeigen ...
                        </button>&nbsp;&nbsp;`;
                            }
                            return result;
                        }
                    }],
                // Add Col BelegNr only if set
                hasBelegNr = !fn.ToArray(data).every(item => !item.DocumentNo);
            if (hasBelegNr) {
                cols.splice(0, 0, {
                    field: "DocumentNo", title: "Belegnr."
                });
            }
            this.GridOptions = <kendo.ui.GridOptions>{ dataSource: data, columns: cols };
        }
        this.trigger('RefreshData');
    }
    readonly data: IObservableArray<IAddonData> = AsObservableArray([])
}


function GetDefaultOpenDocInfoExt($c: CompatibilityService): ISPAOpenExtInfo {
    if (!defaultOpenDocInfoExt) {
        const $injector = angular.element(document).injector();
        var ctrl = SPAProxyController.TryInstantiate($c, "OpenDocInfo");
        if (ctrl) {
            defaultOpenDocInfoExt = {
                Ctrl: ctrl,
                OpenExt: false,
            }
        $injector.get("$rootScope").$on("$routeChangeStart", () => {
            var bak = defaultOpenDocInfoExt.OpenExt;
            ctrl.IsActive = false;
            window.setTimeout(() => {
                defaultOpenDocInfoExt.OpenExt = bak;
            }, 1000);
        });
        }
        ctrl.bind(ExternalView.EVENT_CLOSE, () => { defaultOpenDocInfoExt.OpenExt = false });
    }
    return defaultOpenDocInfoExt;
}
var defaultOpenDocInfoExt: ISPAOpenExtInfo;

export function OpenDocInfo(info: JQueryEventObject | string, openExt?: ISPAOpenExtInfo): PromiseLike<any> {
    const $injector = angular.element(document).injector();
    const $c = $injector.get("CompatibilityService");
    if (!info)
        return;
    if (typeof info === "string") {
        var docid = info;
    } else {
        var grid = <kendo.ui.Grid>$(info.target || info.srcElement).parents(":data('kendoGrid')").data("kendoGrid");
        let rowItem = GetRowItem(info);
        info.cancelBubble = true;

        docid = rowItem && rowItem.get("WorkflowID");
    }
    if (!docid)
        return;

    let checkOpenLocal = $c.when(true);
    const DialogService = <DialogService>$c.resolve("DialogService");
    if (docid == "{00000000-0000-0000-0000-000000000000}") {
        return DialogService.ShowError('Keine Belegauskunft vorhanden!');
    }
    openExt = openExt && openExt.Ctrl ? openExt : GetDefaultOpenDocInfoExt($c);
    if (openExt?.Ctrl) {
        var TryOpenExternal = (force?: boolean) => {
            return $c.when(force || openExt.OpenExt).then(s => {
                s = s === true;
                if (s) {
                    return $c.when(openExt.Ctrl.ChangeRoute('/#/docinfo/' + docid)).then(() => {
                        return openExt.OpenExt = true;
                    }, err => false)
                }
                return s;
            })
        }

        checkOpenLocal = checkOpenLocal.then(() => TryOpenExternal().then(opened => !opened));
    }

    return checkOpenLocal.then((openLocal) => {
        if (openLocal === false) {
            return;
        }
        const width = $(window).width() - 60;
        const height = $(window).height() - 100;
        const showWindow = fn.UISafe(DialogService.ShowWindow({
            controller: "CtrlDocumentInfo",
            template: require("./documentinfo/documentinfo.fragment.html"),
            windowOptions: <kendo.ui.WindowOptions>{
                modal: true,
                resizable: true,
                actions: [TryOpenExternal ? "hyperlink-open" : "", "Undo", "Maximize", "Close"].filter(fn.isSet),
                width: width,
                height: height,
                title: "Belegauskunft"
            },
            titleFormula: "'Belegauskunft' + (WFInstanceCompletedData.EntryNo ? ' für Workflow Nr. ' + WFInstanceCompletedData.EntryNo : '')",
            resolve: {
                "WFInstanceCompletedResult": $c.all(<IPageQueryParameters<IFDRW_WFInstanceCompleted>>{
                    PageName: Pages.FDRW_WFInstanceCompleted.PageName,
                    Constraints: [{ FieldName: "ID", Value: docid }],
                    PageCount: 2,
                }).then(itemKey => $c.resolve("NavClient").ReadMultiple<IFDRW_WFInstanceCompleted>(itemKey).then(d => {
                    if (!(d.Data?.length === 1)) {
                        throw new Error("Zu '" + docid + "' sind keine Daten vorhanden!");
                    }
                    return <INavReadResult<IFDRW_WFInstanceCompleted>>{
                        Client: d.Client,
                        Data: d.Data[0]
                    };
                }).then(fn.identity, err => {
                    DialogService.ShowError(fn.resolveToError(err));
                    fn.error.ThrowCancel();
                })
                )
            }
        })).then(w => {
            // TODO : More Generic way ?
            var undoEl = w.window.wrapper.find(".k-window-actions [aria-label='Undo']").attr("title", "Einstellungen zurücksetzen").bind("click.navCustom", function (e) {
                e.preventDefault();
                DialogService.ShowConfirm({ message: "Einstellungen zurücksetzen?" }).then(d => d && w.scope["ResetUserSettings"]());
            }).parent().removeAttr("href"); // href : s.times href='#' ...;
            if (TryOpenExternal) {
                var popoutEl = w.window.wrapper.find(".k-window-actions [aria-label='hyperlink-open']").attr("title", "In separatem Fenster öffnen").bind("click.navCustom", function (e) {
                    e.preventDefault();
                    TryOpenExternal(true).then(gotOpenedExternal => {
                        if (gotOpenedExternal) {
                            w.window.close();
                        }
                    });
                }).parent().removeAttr("href"); // href : s.times href='#' ...;
            }
            w.scope.$on("$destroy", () => undoEl.add(popoutEl).unbind(".navCustom"));
            return w;
        });
        // Show Busy Indicator, Hide on Loaded, or window closed
        if (grid) {
            $(grid.element).kendoNavBusyIndicator(true);
            var endBusy = <() => void>fn.once(() => $(grid.element).kendoNavBusyIndicator(false));
            showWindow.then(w => {
                w.close.then(endBusy, endBusy);
                w.scope.$on("DocumentInfoInitialized", endBusy);
            }, endBusy);
        }
    });
}
